import { type ReactNode } from 'react';

import { cn, Flex } from '@kognitos/lattice';

/**
 * Props for the BrowserChrome component.
 */
export interface IBrowserChromeProps {
  /** Content to render inside the browser content area. */
  children?: ReactNode;
  /** Additional CSS classes. */
  className?: string;
}

/**
 * BrowserChrome component that mimics a browser window appearance.
 *
 * Includes:
 * - Title bar with traffic light buttons and tab
 * - Address bar with navigation controls
 * - Content area for the actual browser content (screencast viewer)
 *
 * @param props - Component props
 * @returns BrowserChrome component
 *
 * @example
 * <BrowserChrome>
 *   <ScreencastViewer screencastUrl="https://..." />
 * </BrowserChrome>
 */
export function BrowserChrome({ children, className }: IBrowserChromeProps) {
  return (
    <Flex
      direction="column"
      width="full"
      className={cn(
        'rounded-md overflow-hidden border border-border',
        className,
      )}
    >
      {/* Title Bar - Dark background with traffic lights and tab */}
      <Flex align="center" width="full" className="bg-muted h-8 px-3 shrink-0">
        {/* Traffic Light Buttons */}
        <Flex gap={2} align="center" className="shrink-0">
          <div
            className="w-3 h-3 rounded-full bg-destructive"
            data-testid="traffic-light-close"
          />
          <div
            className="w-3 h-3 rounded-full bg-warning"
            data-testid="traffic-light-minimize"
          />
          <div
            className="w-3 h-3 rounded-full bg-success"
            data-testid="traffic-light-maximize"
          />
        </Flex>
      </Flex>

      {/* Content Area - 16:9 so screencast frames fill with no gap */}
      <Flex
        direction="column"
        width="full"
        className="aspect-video bg-background min-h-0"
      >
        {children}
      </Flex>
    </Flex>
  );
}
